import { useMemo, useState } from 'react';
import { MagnifyingGlassIcon } from '@phosphor-icons/react';

import { Card } from 'components/Card';
import { Input } from 'components/input/Input';
import { DelayedSpinner } from 'components/Spinner';
import { Table } from 'components/table/Table';
import { EmptyState } from 'components/empty-state/EmptyState';
import { ToggleGroup } from 'components/toggle-group/ToggleGroup';
import classNames from '@utils/classnames';
import { formatDateTime } from '@utils/date';
import { AuditLogActionType } from 'src/types/enums';
import { useUserActivityStats } from 'src/app/audit-log/models';
import type { UserActivityUserDetail } from 'src/app/audit-log/endpoints/UserActivityStatsEndpoint.types';
import { useDashboardContext } from '../context';
import { UserActivityTimeline } from '../components/user-activity/UserActivityTimeline';
import {
  ACTION_TYPE_FILTERS,
  type ActionTypeFilterOption,
  filterUserDetail,
  formatPeriodDuration,
} from '../components/user-activity/user-activity.utils';
import { ErrorState } from 'components/error-state/ErrorState';
import { StatCard } from 'components/stat-card/StatCard';

const ACTION_TYPE_TOGGLE_OPTIONS: { value: ActionTypeFilterOption; label: string }[] = [
  { value: 'all', label: 'Alle acties' },
  ...ACTION_TYPE_FILTERS.map((actionType) => ({ value: actionType, label: actionType })),
];

function UserListItem({
  title,
  subtitle,
  meta,
  isSelected,
  onClick,
}: {
  title: string;
  subtitle: string;
  meta?: string;
  isSelected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={classNames('w-full text-left px-4 py-3 border-b border-border transition-colors', {
        'bg-brand-subtle text-fg': isSelected,
        'hover:bg-surface-hover text-fg': !isSelected,
      })}
      onClick={onClick}
    >
      <div className="font-medium wrap-break-word">{title}</div>
      <div className="text-sm text-fg-secondary mt-0.5">{subtitle}</div>
      {meta && <div className="text-xs text-fg-muted mt-1">{meta}</div>}
    </button>
  );
}

function UserDetailPanel({ user }: { user: UserActivityUserDetail }) {
  return (
    <div className="flex flex-col gap-4">
      <Card title={user.actorName}>
        <p className="text-sm text-fg-secondary">Gebruiker activiteit in geselecteerde periode</p>
        <div className="flex flex-wrap gap-3 mt-4">
          <StatCard variant="compact" className="flex-1 min-w-40" label="Totaal acties" value={user.totalActions} />
          <StatCard variant="compact" className="flex-1 min-w-40" label="Logins" value={user.loginCount} />
          <StatCard
            variant="compact"
            className="flex-1 min-w-40"
            label="Actieve periodes"
            value={user.activePeriods.length}
          />
          <StatCard
            variant="compact"
            className="flex-1 min-w-40"
            label="Laatste login"
            value={user.lastLoginAt ? formatDateTime(user.lastLoginAt) : '-'}
          />
          <StatCard
            variant="compact"
            className="flex-1 min-w-40"
            label="Laatste activiteit"
            value={user.lastActivityAt ? formatDateTime(user.lastActivityAt) : '-'}
          />
        </div>
      </Card>

      <Card title="Acties per type">
        {user.actionCountsByType.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {user.actionCountsByType.map((item) => (
              <StatCard variant="compact" key={item.actionType} label={item.actionType} value={item.count} />
            ))}
          </div>
        ) : (
          <EmptyState title="Geen acties voor deze filters" />
        )}
      </Card>

      <Card title="Login-log" flush>
        {user.logins.length > 0 ? (
          <Table
            idKey="createdAt"
            headers={[
              { id: 'createdAt', name: 'Datum en tijd' },
              { id: 'description', name: 'Beschrijving' },
            ]}
            data={user.logins}
            mapData={(login) => [formatDateTime(login.createdAt), login.description]}
          />
        ) : (
          <EmptyState title="Geen logins in deze periode" />
        )}
      </Card>

      <Card
        title="Actieve periodes"
        subtitle="Opeenvolgende audit log entries binnen 30 minuten worden als één actieve periode geteld."
        flush
      >
        {user.activePeriods.length > 0 ? (
          <Table
            idKey="startAt"
            headers={[
              { id: 'startAt', name: 'Van' },
              { id: 'endAt', name: 'Tot' },
              { id: 'duration', name: 'Duur' },
              { id: 'actionCount', name: 'Acties' },
            ]}
            data={user.activePeriods}
            mapData={(period) => [
              formatDateTime(period.startAt),
              formatDateTime(period.endAt),
              formatPeriodDuration(period.startAt, period.endAt),
              period.actionCount,
            ]}
          />
        ) : (
          <EmptyState title="Geen actieve periodes gevonden" />
        )}
      </Card>

      <Card title="Activiteitstijdlijn">
        <UserActivityTimeline entries={user.timeline} />
      </Card>
    </div>
  );
}

export function UserActivityStats() {
  const { timeFrame } = useDashboardContext();
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [actionTypeFilter, setActionTypeFilter] = useState<ActionTypeFilterOption>('all');
  const [userSearch, setUserSearch] = useState('');

  const filters = useMemo(() => {
    if (!timeFrame?.startDate || !timeFrame?.endDate) {
      return null;
    }

    return {
      from: timeFrame.startDate,
      to: timeFrame.endDate,
    };
  }, [timeFrame]);

  const { data, error, isValidating, mutate } = useUserActivityStats(filters);

  const filteredUsers = useMemo(() => {
    if (!data) {
      return [];
    }

    const search = userSearch.trim().toLowerCase();

    return data.users
      .map((user) => filterUserDetail(user, actionTypeFilter))
      .filter((user) => {
        if (user.totalActions === 0 && actionTypeFilter !== 'all') {
          return false;
        }

        if (!search) {
          return true;
        }

        return user.actorName.toLowerCase().includes(search);
      });
  }, [data, actionTypeFilter, userSearch]);

  const selectedUser = useMemo(() => {
    if (!selectedUserId) {
      return null;
    }

    return filteredUsers.find((user) => user.actorId === selectedUserId) ?? null;
  }, [filteredUsers, selectedUserId]);

  const overviewRows = useMemo(() => {
    return filteredUsers.map((user) => {
      const created =
        user.actionCountsByType.find((item) => item.actionType === AuditLogActionType.Created)?.count ?? 0;
      const updated =
        user.actionCountsByType.find((item) => item.actionType === AuditLogActionType.Updated)?.count ?? 0;
      const deleted =
        user.actionCountsByType.find((item) => item.actionType === AuditLogActionType.Deleted)?.count ?? 0;
      const read = user.actionCountsByType.find((item) => item.actionType === AuditLogActionType.Read)?.count ?? 0;

      return {
        ...user,
        created,
        updated,
        deleted,
        read,
      };
    });
  }, [filteredUsers]);

  const globalActionCounts = useMemo(() => {
    const counts = new Map<string, number>();

    for (const user of filteredUsers) {
      for (const item of user.actionCountsByType) {
        counts.set(item.actionType, (counts.get(item.actionType) ?? 0) + item.count);
      }
    }

    return [...counts.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([actionType, count]) => ({ actionType, count }));
  }, [filteredUsers]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h2 className="heading-two">Gebruikersactiviteit</h2>
        <p className="text-xs text-fg-muted">
          Selecteer een gebruiker om login-log, acties per type en een tijdlijn te bekijken.
        </p>
      </div>

      <div>
        <div className="label-text mb-1.5">Actie-type</div>
        <ToggleGroup options={ACTION_TYPE_TOGGLE_OPTIONS} value={actionTypeFilter} onChange={setActionTypeFilter} />
      </div>

      {isValidating && !data && <DelayedSpinner size={8} className="flex justify-center py-16" />}

      {error && <ErrorState error={error} onRetry={() => mutate()} variant="section" />}

      {data && (
        <>
          <div className="flex flex-wrap gap-4">
            <StatCard variant="compact" className="flex-1 min-w-40" label="Gebruikers" value={filteredUsers.length} />
            <StatCard
              variant="compact"
              className="flex-1 min-w-40"
              label="Totaal acties"
              value={filteredUsers.reduce((sum, user) => sum + user.totalActions, 0)}
            />
            <StatCard
              variant="compact"
              className="flex-1 min-w-40"
              label="Logins"
              value={filteredUsers.reduce((sum, user) => sum + user.loginCount, 0)}
            />
          </div>

          <div className="grid grid-cols-1 items-start gap-4 xl:grid-cols-[290px_minmax(0,1fr)]">
            <Card title="Gebruikers">
              <Input
                type="text"
                value={userSearch}
                onChange={setUserSearch}
                placeholder="Zoek op naam of e-mail..."
                iconLeft={<MagnifyingGlassIcon className="w-4 h-4 text-fg-muted" />}
              />

              <div className="-mx-4 -mb-4 mt-4 max-h-[640px] overflow-y-auto border-t border-border">
                <UserListItem
                  title="Alle gebruikers"
                  subtitle="Overzichtstabel"
                  isSelected={selectedUserId === null}
                  onClick={() => setSelectedUserId(null)}
                />

                {filteredUsers.length > 0 ? (
                  filteredUsers.map((user) => (
                    <UserListItem
                      key={user.actorId}
                      title={user.actorName}
                      subtitle={`${user.totalActions} acties · ${user.loginCount} logins`}
                      meta={`Laatste activiteit: ${user.lastActivityAt ? formatDateTime(user.lastActivityAt) : '-'}`}
                      isSelected={selectedUserId === user.actorId}
                      onClick={() => setSelectedUserId(user.actorId)}
                    />
                  ))
                ) : (
                  <div className="p-4 text-sm text-fg-secondary">Geen gebruikers gevonden</div>
                )}
              </div>
            </Card>

            <main className="min-w-0">
              {selectedUser ? (
                <UserDetailPanel user={selectedUser} />
              ) : overviewRows.length > 0 ? (
                <div className="flex flex-col gap-4">
                  <Card title="Overzicht per gebruiker" flush>
                    <Table
                      idKey="actorId"
                      headers={[
                        { id: 'actorName', name: 'Gebruiker' },
                        { id: 'created', name: 'Created' },
                        { id: 'updated', name: 'Updated' },
                        { id: 'deleted', name: 'Deleted' },
                        { id: 'read', name: 'Read' },
                        { id: 'loginCount', name: 'Logins' },
                        { id: 'lastLoginAt', name: 'Laatste login' },
                        { id: 'lastActivityAt', name: 'Laatste activiteit' },
                      ]}
                      data={overviewRows}
                      mapData={(user) => [
                        <button
                          type="button"
                          className="link-text text-left"
                          onClick={() => setSelectedUserId(user.actorId)}
                        >
                          {user.actorName}
                        </button>,
                        user.created,
                        user.updated,
                        user.deleted,
                        user.read,
                        user.loginCount,
                        user.lastLoginAt ? formatDateTime(user.lastLoginAt) : '-',
                        user.lastActivityAt ? formatDateTime(user.lastActivityAt) : '-',
                      ]}
                    />
                  </Card>

                  <Card title="Acties per type (totaal)">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {globalActionCounts.map((item) => (
                        <StatCard variant="compact" key={item.actionType} label={item.actionType} value={item.count} />
                      ))}
                    </div>
                  </Card>
                </div>
              ) : (
                <EmptyState title="Geen activiteit in deze periode" />
              )}
            </main>
          </div>
        </>
      )}
    </div>
  );
}
